import React, { useEffect, useState, useMemo } from "react";
import { Trash2, Edit, Search } from "lucide-react";
import axios from "axios";
import { debounce } from "lodash";
import Apiurl from "../../environment/Apiurl";

function Treatment() {
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntries, setShowEntries] = useState("10");
  const [userPrivileges, setUserPrivileges] = useState({});
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);

  // Form state for adding
  const [addData, setAddData] = useState({
    type: "",
    charge_per_treatment: "",
    maximum_discount_percentage: "",
    category: "",
  });

  // Form state for editing
  const [updateData, setUpdateData] = useState({
    type: "",
    charge_per_treatment: "",
    maximum_discount_percentage: "",
    category: "",
  });

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // Fetch user privileges based on role_id
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId);

      if (userRoleId === "unknown") {
        console.warn("No role_id found in localStorage");
        setError(
          "No role assigned to the user. Please contact the administrator."
        );
        setTimeout(() => setError(null), 3000);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log("Fetched roles data:", response.data);

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid or empty roles data received");
      }

      const roleData = response.data.find(
        (role) => role.role_id === userRoleId
      );

      if (roleData) {
        console.log("Found role data for role_id:", userRoleId, roleData);
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page;
          const pagePrivileges = page.priviallages || page.privileges || {};
          privileges[pageName] = {
            access: page.access,
            ...pagePrivileges,
          };
        });
        console.log("Set user privileges:", privileges);
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_id:", userRoleId);
        setError("No role data found for the current user.");
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error(
        "Error fetching user privileges:",
        error.message,
        error.response?.data
      );
      setError(
        error.response?.data?.message ||
          "Failed to fetch user privileges. Please try again."
      );
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check if user has specific permission for Treatment Charges page
  const hasPermission = (permission) => {
    const treatmentPrivileges = userPrivileges["Treatment Charges"];
    if (!treatmentPrivileges) {
      console.log("No privileges found for Treatment Charges page");
      return false;
    }
    console.log(
      `Checking permission '${permission}' for Treatment Charges:`,
      treatmentPrivileges
    );
    if (treatmentPrivileges.all) return true;
    const hasPerm = treatmentPrivileges[permission] || false;
    console.log(`Permission '${permission}' result:`, hasPerm);
    return hasPerm;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Fetch data from API
  const fetchData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Treatment Charges page");
      setError("You do not have permission to view treatment charges.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/treatment_charge`);
      console.log("Fetched treatment charges data:", response.data);

      const reversedData = (response.data || []).reverse();
      setResponseData(reversedData);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching treatment charges:",
        error.message,
        error.response?.data
      );
      setError(
        error.response?.data?.message ||
          "Failed to fetch treatment charges. Please try again."
      );
      setResponseData([]);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial data
  useEffect(() => {
    console.log("Fetching initial data...");
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    console.log(
      "Checking read permission after privileges loaded:",
      userPrivileges
    );
    if (hasPermission("read")) {
      console.log("Fetching treatment charges data due to read permission");
      fetchData();
    } else if (!loadingPrivileges) {
      console.warn("Read permission denied for Treatment Charges page");
      setError("You do not have permission to view treatment charges.");
      setTimeout(() => setError(null), 3000);
    }
  }, [userPrivileges, loadingPrivileges]);

  // Reset current page on search or items per page change
  useEffect(() => {
    console.log(
      "Resetting current page due to searchTerm or itemsPerPage change"
    );
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Handle input changes for add form
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Add form input changed: ${name} = ${value}`);
    setAddData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle input changes for edit form
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Edit form input changed: ${name} = ${value}`);
    setUpdateData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Debounced search handler
  const debouncedSetSearchTerm = debounce((value) => {
    console.log("Search term changed to:", value);
    setSearchTerm(value);
  }, 300);

  const handleSearchChange = (e) => {
    debouncedSetSearchTerm(e.target.value);
  };

  // Validate form data
  const validateForm = (data) => {
    if (!data.type) return "Type is required.";
    if (!data.charge_per_treatment) return "Charge per treatment is required.";
    if (!data.maximum_discount_percentage)
      return "Maximum discount percentage is required.";
    if (!data.category) return "Category is required.";
    const charge = Number(data.charge_per_treatment);
    const discount = Number(data.maximum_discount_percentage);
    if (isNaN(charge) || charge <= 0)
      return "Charge per treatment must be a positive number.";
    if (isNaN(discount) || discount < 0 || discount > 100)
      return "Maximum discount percentage must be between 0 and 100.";
    return null;
  };

  // Handle add form submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Treatment Charges page");
      setError("You do not have permission to add treatment charges.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(addData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const formData = {
        type: addData.type,
        charge_per_treatment: Number(addData.charge_per_treatment),
        maximum_discount_percentage: Number(
          addData.maximum_discount_percentage
        ),
        category: addData.category,
      };
      console.log("Adding treatment charge with data:", formData);
      await axios.post(`${Apiurl}/treatment_charge`, formData);
      fetchData();
      closeAddModal();
    } catch (error) {
      console.error(
        "Error adding treatment charge:",
        error.message,
        error.response?.data
      );
      setError(
        error.response?.data?.message ||
          "Failed to add treatment charge. Please try again."
      );
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Treatment Charges page");
      setError("You do not have permission to edit treatment charges.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!editingId) {
      console.warn("Invalid treatment ID:", editingId);
      setError("Invalid treatment ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(updateData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const formData = {
        type: updateData.type,
        charge_per_treatment: Number(updateData.charge_per_treatment),
        maximum_discount_percentage: Number(
          updateData.maximum_discount_percentage
        ),
        category: updateData.category,
      };
      console.log(
        "Updating treatment charge with ID:",
        editingId,
        "Data:",
        formData
      );
      await axios.put(`${Apiurl}/treatment_charge/${editingId}`, formData);
      fetchData();
      closeEditModal();
    } catch (error) {
      console.error(
        "Error updating treatment charge:",
        error.message,
        error.response?.data
      );
      setError(
        error.response?.data?.message ||
          "Failed to update treatment charge. Please try again."
      );
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Reset add form
  const resetAddForm = () => {
    console.log("Resetting add form");
    setAddData({
      type: "",
      charge_per_treatment: "",
      maximum_discount_percentage: "",
      category: "",
    });
  };

  // Edit treatment charge
  const handleEdit = (id, treatment) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Treatment Charges page");
      setError("You do not have permission to edit treatment charges.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid treatment ID:", id);
      setError("Invalid treatment ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log(
      "Opening Edit Treatment modal for ID:",
      id,
      "Treatment:",
      treatment
    );
    setEditingId(id);
    setUpdateData({
      type: treatment.type || "",
      charge_per_treatment: treatment.charge_per_treatment || "",
      maximum_discount_percentage: treatment.maximum_discount_percentage || "",
      category: treatment.category || "",
    });
    setShowEditModal(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Treatment Charges page");
      setError("You do not have permission to delete treatment charges.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid treatment ID:", id);
      setError("Invalid treatment ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Delete Treatment modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    console.log("Closing Delete Treatment modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  // Handle delete treatment charge
  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Treatment Charges page");
      setError("You do not have permission to delete treatment charges.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid treatment ID:", deleteId);
      setError("Invalid treatment ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Deleting treatment charge with ID:", deleteId);
      await axios.delete(`${Apiurl}/treatment_charge/${deleteId}`);
      fetchData();
      closeDeleteModal();
    } catch (error) {
      console.error(
        "Error deleting treatment charge:",
        error.message,
        error.response?.data
      );
      setError(
        error.response?.data?.message ||
          "Failed to delete treatment charge. Please try again."
      );
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Modal handlers
  const openAddModal = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Treatment Charges page");
      setError("You do not have permission to add treatment charges.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Add Treatment modal");
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    console.log("Closing Add Treatment modal");
    setShowAddModal(false);
    resetAddForm();
    setError(null);
  };

  const closeEditModal = () => {
    console.log("Closing Edit Treatment modal");
    setShowEditModal(false);
    setEditingId(null);
    setUpdateData({
      type: "",
      charge_per_treatment: "",
      maximum_discount_percentage: "",
      category: "",
    });
    setError(null);
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    const result = responseData.filter((item) =>
      [
        item.type,
        item.charge_per_treatment?.toString(),
        item.maximum_discount_percentage?.toString(),
        item.category,
      ].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    console.log("Filtered data:", result);
    return result;
  }, [responseData, searchTerm]);

  // Update items per page
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 10); // Default to 10 if empty
    } else {
      setItemsPerPage(Number(showEntries));
    }
  }, [showEntries, filteredData]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  console.log(
    "Pagination - totalPages:",
    totalPages,
    "itemsPerPage:",
    itemsPerPage,
    "filteredData.length:",
    filteredData.length
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  console.log(
    "Current items:",
    currentItems,
    "from index",
    indexOfFirstItem,
    "to",
    indexOfLastItem
  );

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    console.log("Generated page list:", pages);
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      console.log("Navigating to page:", page);
      setCurrentPage(page);
    }
  };

  if (loadingPrivileges) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading permissions...</span>
        </div>
      </div>
    );
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_id:",
      localStorage.getItem("role_id"),
      "on Treatment Charges page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <style>{`
        .btn-custom { 
          background-color: #01AEBE; 
          color: white; 
          border: none;
        }
        .btn-custom:hover { 
          background-color: #018A97; 
          color: white;
        }
      `}</style>

      {hasPermission("create") && (
        <div className="text-end">
          <button
            className="btn btn-custom btn-sm"
            onClick={openAddModal}
            aria-label="Add new treatment"
          >
            <i className="fas fa-plus me-2"></i> Add Treatment
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="bg-white rounded shadow-sm">
            <div className="row mb-3 crm-table-toolbar">
              <div className="col-md-2">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-table"></i>
                  </span>
                  <select
                    className="form-select"
                    value={showEntries}
                    onChange={(e) => {
                      console.log(
                        "Changing items per page to:",
                        e.target.value
                      );
                      setShowEntries(e.target.value);
                    }}
                    aria-label="Select number of entries per page"
                  >
                    <option value="All">Show All</option>
                    {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                      <option key={n} value={n}>
                        Show {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-7"></div>
              <div className="col-md-3">
                <div className="d-flex align-items-center">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control pe-5"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={{ width: "250px", paddingRight: "2.5rem" }}
                      aria-label="Search treatment charges"
                    />
                    <Search
                      size={16}
                      className="position-absolute text-muted"
                      style={{
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-100%)",
                      }}
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </div>
            </div>

            {loading && (
              <div className="text-center py-3">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-hover">
                <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                  <tr>
                    <th scope="col">S.No</th>
                    <th scope="col">Type</th>
                    <th scope="col">Charge per Treatment</th>
                    <th scope="col">Category</th>
                    <th scope="col">Max Discount (%)</th>
                    {(hasPermission("update") || hasPermission("delete")) && (
                      <th scope="col">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((treatment, index) => (
                      <tr key={treatment._id || index} className="align-middle">
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>{treatment.type || "-"}</td>
                        <td>{treatment.charge_per_treatment || "-"}</td>
                        <td>{treatment.category || "-"}</td>
                        <td>{treatment.maximum_discount_percentage || "-"}</td>
                        {(hasPermission("update") ||
                          hasPermission("delete")) && (
                            <td>
                              {hasPermission("delete") && (
                                <button
                                  className="btn btn-sm p-1 border-0"
                                  style={{ backgroundColor: "transparent" }}
                                  onClick={() => openDeleteModal(treatment._id)}
                                  aria-label={`Delete treatment ${treatment.type}`}
                                >
                                  <Trash2 size={16} className="text-danger" />
                                </button>
                              )}
                              {hasPermission("update") && (
                                <button
                                  className="btn btn-sm p-1 border-0"
                                  style={{ backgroundColor: "transparent" }}
                                  onClick={() =>
                                    handleEdit(treatment._id, treatment)
                                  }
                                  aria-label={`Edit treatment ${treatment.type}`}
                                >
                                  <Edit size={16} className="text-primary" />
                                </button>
                              )}
                            </td>
                          )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          hasPermission("update") || hasPermission("delete")
                            ? 6
                            : 5
                        }
                        className="text-center py-4"
                      >
                        {loading ? (
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          <div className="text-muted">
                            <i className="fas fa-search fa-2x mb-2"></i>
                            <br />
                            No treatment charges found matching your search
                            criteria
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredData.length > 0 && (
              <div className="container">
                <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      currentPage > 1 && handlePageClick(currentPage - 1)
                    }
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <i className="fas fa-chevron-left"></i>
                    <span className="d-none d-sm-inline">Previous</span>
                  </button>

                  <div
                    className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                    style={{ maxWidth: "100%" }}
                  >
                    {pages.map((page, index) => (
                      <button
                        key={index}
                        className={`btn btn-sm px-3 flex-shrink-0 ${
                          page === currentPage
                            ? "text-white"
                            : "btn-outline-secondary"
                        }`}
                        style={
                          page === currentPage
                            ? { backgroundColor: "#20c997", border: "none" }
                            : {}
                        }
                        onClick={() => handlePageClick(page)}
                        disabled={page === "..."}
                        aria-label={
                          page === "..."
                            ? "More pages"
                            : `Go to page ${page}`
                        }
                        aria-current={page === currentPage ? "page" : undefined}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      currentPage < totalPages && handlePageClick(currentPage + 1)
                    }
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    <span className="d-none d-sm-inline">Next</span>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Treatment Modal */}
      {hasPermission("create") && (

        
        <div
          className={`modal fade ${showAddModal ? "show" : ""}`}
          style={{ display: showAddModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="addTreatmentModalLabel"
          aria-hidden={!showAddModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h6
                  className="modal-title text-dark"
                  id="addTreatmentModalLabel"
                >
                  Add Treatment
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeAddModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="type" className="form-label fw-bold">
                          Type
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="type"
                          name="type"
                          placeholder="Enter Type (e.g., IVF)"
                          value={addData.type}
                          onChange={handleAddInputChange}
                          required
                          aria-required="true"
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="category"
                          className="form-label fw-bold"
                        >
                          Category
                        </label>
                        <select
                          className="form-control"
                          id="category"
                          name="category"
                          value={addData.category}
                          onChange={handleAddInputChange}
                          required
                          aria-required="true"
                        >
                          <option value="">Select Category</option>
                          <option value="Yoga">Yoga</option>
                          <option value="Homeopathy">Homeopathy</option>
                          <option value="General">General</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="charge_per_treatment"
                          className="form-label fw-bold"
                        >
                          Charge per Treatment
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="charge_per_treatment"
                          name="charge_per_treatment"
                          placeholder="Enter Charge"
                          value={addData.charge_per_treatment}
                          onChange={handleAddInputChange}
                          min="0"
                          step="0.01"
                          required
                          aria-required="true"
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="maximum_discount_percentage"
                          className="form-label fw-bold"
                        >
                          Max Discount (%)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="maximum_discount_percentage"
                          name="maximum_discount_percentage"
                          placeholder="Enter Max Discount"
                          value={addData.maximum_discount_percentage}
                          onChange={handleAddInputChange}
                          min="0"
                          max="100"
                          step="0.01"
                          required
                          aria-required="true"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-sm px-3 me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeAddModal}
                      aria-label="Cancel adding treatment"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-custom btn-sm px-4"
                      disabled={loading}
                      aria-label="Submit new treatment"
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Treatment Modal */}
      {hasPermission("update") && (
        <div
          className={`modal fade ${showEditModal ? "show" : ""}`}
          style={{ display: showEditModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="editTreatmentModalLabel"
          aria-hidden={!showEditModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h6
                  className="modal-title text-dark"
                  id="editTreatmentModalLabel"
                >
                  Edit Treatment
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="type" className="form-label fw-bold">
                          Type
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="type"
                          name="type"
                          placeholder="Enter Type (e.g., IVF)"
                          value={updateData.type}
                          onChange={handleEditInputChange}
                          required
                          aria-required="true"
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="category"
                          className="form-label fw-bold"
                        >
                          Category
                        </label>
                        <select
                          className="form-control"
                          id="category"
                          name="category"
                          value={updateData.category}
                          onChange={handleEditInputChange} // Fixed handler
                          required
                          aria-required="true"
                        >
                          <option value="">Select Category</option>
                          <option value="Yoga">Yoga</option>
                          <option value="Homeopathy">Homeopathy</option>
                          <option value="General">General</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="charge_per_treatment"
                          className="form-label fw-bold"
                        >
                          Charge per Treatment
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="charge_per_treatment"
                          name="charge_per_treatment"
                          placeholder="Enter Charge"
                          value={updateData.charge_per_treatment}
                          onChange={handleEditInputChange}
                          min="0"
                          step="0.01"
                          required
                          aria-required="true"
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="maximum_discount_percentage"
                          className="form-label fw-bold"
                        >
                          Max Discount (%)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="maximum_discount_percentage"
                          name="maximum_discount_percentage"
                          placeholder="Enter Max Discount"
                          value={updateData.maximum_discount_percentage}
                          onChange={handleEditInputChange}
                          min="0"
                          max="100"
                          step="0.01"
                          required
                          aria-required="true"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-sm px-3 me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeEditModal}
                      aria-label="Cancel editing treatment"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-custom btn-sm px-4"
                      disabled={loading}
                      aria-label="Update treatment"
                    >
                      {loading ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteTreatmentModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ color: "#dc2626", fontSize: "20px" }}
                    ></i>
                  </span>
                </div>
                <h5 className="modal-title" id="deleteTreatmentModalLabel">
                  Delete Treatment
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to delete this treatment charge?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                  aria-label="Cancel deletion"
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={confirmDelete}
                  disabled={loading}
                  aria-label="Confirm deletion"
                >
                  {loading ? "Deleting..." : "Yes, Delete It!"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal || showEditModal || showDeleteModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default Treatment;